"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const ts_invariant_1 = __importDefault(require("ts-invariant"));
/**
 * This is the number of client (screen) pixels that a press can move before it
 * is not considered a tap.
 */
const POTENTIAL_TAP_BOUNDS_DEFAULT = 8;
/**
 * If you are using `Space` then you don't need to use or interact directly with this class.  It is used
 * internally by `Space` along with `Pressable` to interpret and respond to presses.
 *
 * On the other hand if you are using `ViewPort` without `Space` you may want to use this to
 * make handling interactions easier.
 *
 * It works by calling a `DecidePressHandlingCallback` callback whenever a
 * press starts, and the callback decides how the press should be handled.
 *
 * After you construct the `PressInterpreter` pass the `pressHandlers` to
 * the `ViewPort`'s constructors (as part of the `options` parameter).
 */
class PressInterpreter {
    /**
     * If you are using a `Space` you do not need to create one of these, but
     * if you aren't using a `Space` this should be done before the
     * `ViewPort` is created, and then the `pressHandlers` should be passed
     * to the `ViewPort`'s `ViewPortOptions`.
     *
     * @param onDecideHowToHandlePress This callback decides how to handle presses.  See `DecidePressHandlingCallback` for more info.
     */
    constructor(onDecideHowToHandlePress) {
        this.onDecideHowToHandlePress = onDecideHowToHandlePress;
        this.handleCapturePressThresholdMet = () => {
            var _a, _b, _c;
            this.capturePressTimerId = undefined;
            this.currentPressCapturedForHandler = true;
            ts_invariant_1.default(this.currentPressLastCoordinates !== undefined, `When the timer fires we should have press coordinates.`);
            (_c = (_a = this.currentConfig) === null || _a === void 0 ? void 0 : (_b = _a).onCapturePressStart) === null || _c === void 0 ? void 0 : _c.call(_b, this.currentPressLastCoordinates);
            // Cancel long press
            this.currentPressLongPressThresholdMet = false;
            if (this.longPressTimerId) {
                clearTimeout(this.longPressTimerId);
                this.longPressTimerId = undefined;
            }
        };
        this.handleLongPressThresholdMet = () => {
            var _a, _b, _c;
            this.longPressTimerId = undefined;
            this.currentPressLongPressThresholdMet = true;
            ts_invariant_1.default(this.currentPressLastCoordinates !== undefined, `When the timer fires we should have press coordinates.`);
            (_c = (_a = this.currentConfig) === null || _a === void 0 ? void 0 : (_b = _a).onPotentialLongTap) === null || _c === void 0 ? void 0 : _c.call(_b, this.currentPressLastCoordinates);
        };
        this.handlePressStart = (e, coordinates) => {
            var _a, _b;
            if (this.currentConfig) {
                this.reset();
            }
            this.currentConfig = this.onDecideHowToHandlePress(e, coordinates);
            if (this.currentConfig) {
                this.currentPressStartingCoordinates = coordinates;
                this.currentPressLastCoordinates = coordinates;
                this.currentPressLongPressThresholdMet = false;
                if (this.currentConfig.ignorePressEntirely) {
                    return 'ignore';
                }
                if (this.currentConfig.capturePressThresholdMs === 0) {
                    (_b = (_a = this.currentConfig).onCapturePressStart) === null || _b === void 0 ? void 0 : _b.call(_a, coordinates);
                    this.currentPressCapturedForHandler = true;
                }
                else {
                    if (this.currentConfig.onTap && this.currentConfig.onPotentialTap) {
                        this.currentConfig.onPotentialTap(coordinates);
                    }
                    if (this.currentConfig.capturePressThresholdMs !== undefined) {
                        this.capturePressTimerId = setTimeout(this.handleCapturePressThresholdMet, this.currentConfig.capturePressThresholdMs);
                    }
                    if (this.currentConfig.longTapThresholdMs !== undefined) {
                        this.longPressTimerId = setTimeout(this.handleLongPressThresholdMet, this.currentConfig.longTapThresholdMs);
                    }
                }
                return 'capture';
            }
            return undefined;
        };
        this.handlePressMove = (e, coordinates) => {
            var _a, _b, _c, _d, _e;
            if (!this.currentConfig ||
                this.currentConfig.ignorePressEntirely ||
                !this.currentPressStartingCoordinates ||
                !this.currentPressLastCoordinates) {
                return undefined;
            }
            const oldCoordinates = this.currentPressLastCoordinates;
            this.currentPressLastCoordinates = coordinates;
            if (this.currentPressCapturedForHandler) {
                (_b = (_a = this.currentConfig).onCapturePressMove) === null || _b === void 0 ? void 0 : _b.call(_a, Object.assign(Object.assign({}, coordinates), { clientXDelta: coordinates.clientX - oldCoordinates.clientX, clientYDelta: coordinates.clientY - oldCoordinates.clientY, containerXDelta: coordinates.containerX - oldCoordinates.containerX, containerYDelta: coordinates.containerY - oldCoordinates.containerY, xDelta: coordinates.x - oldCoordinates.x, yDelta: coordinates.y - oldCoordinates.y }));
                return;
            }
            const xDelta = Math.abs(coordinates.containerX - this.currentPressStartingCoordinates.containerX);
            const yDelta = Math.abs(coordinates.containerY - this.currentPressStartingCoordinates.containerY);
            const maxDeltaAllowed = (_c = this.currentConfig.potentialTapBounds, (_c !== null && _c !== void 0 ? _c : POTENTIAL_TAP_BOUNDS_DEFAULT));
            if (xDelta > maxDeltaAllowed || yDelta > maxDeltaAllowed) {
                (_e = (_d = this.currentConfig).onTapAbandoned) === null || _e === void 0 ? void 0 : _e.call(_d);
                this.reset();
                return 'release';
            }
            return undefined;
        };
        this.handlePressEnd = (e, coordinates) => {
            var _a, _b, _c, _d, _e, _f;
            if (!this.currentConfig || this.currentConfig.ignorePressEntirely) {
                this.reset();
                return;
            }
            if (this.currentPressCapturedForHandler) {
                // It should be impossible for this event to fire without another event
                // which sets this
                const oldCoordinates = this.currentPressLastCoordinates;
                (_b = (_a = this.currentConfig).onCapturePressEnd) === null || _b === void 0 ? void 0 : _b.call(_a, Object.assign(Object.assign({}, coordinates), { clientXDelta: coordinates.clientX - oldCoordinates.clientX, clientYDelta: coordinates.clientY - oldCoordinates.clientY, containerXDelta: coordinates.containerX - oldCoordinates.containerX, containerYDelta: coordinates.containerY - oldCoordinates.containerY, xDelta: coordinates.x - oldCoordinates.x, yDelta: coordinates.y - oldCoordinates.y }));
            }
            else if (this.currentPressLongPressThresholdMet) {
                (_d = (_c = this.currentConfig).onLongTap) === null || _d === void 0 ? void 0 : _d.call(_c, coordinates);
            }
            else {
                (_f = (_e = this.currentConfig).onTap) === null || _f === void 0 ? void 0 : _f.call(_e, coordinates);
            }
            this.reset();
        };
        this.handlePressCancel = (e) => {
            var _a, _b, _c, _d, _e, _f;
            if (this.currentPressCapturedForHandler) {
                (_c = (_a = this.currentConfig) === null || _a === void 0 ? void 0 : (_b = _a).onCapturePressCancelled) === null || _c === void 0 ? void 0 : _c.call(_b);
            }
            else {
                (_f = (_d = this.currentConfig) === null || _d === void 0 ? void 0 : (_e = _d).onTapAbandoned) === null || _f === void 0 ? void 0 : _f.call(_e);
            }
            this.reset();
        };
        this.reset = () => {
            this.currentConfig = undefined;
            this.currentPressStartingCoordinates = undefined;
            this.currentPressLastCoordinates = undefined;
            this.currentPressLongPressThresholdMet = undefined;
            this.currentPressCapturedForHandler = undefined;
            if (this.capturePressTimerId) {
                clearTimeout(this.capturePressTimerId);
                this.capturePressTimerId = undefined;
            }
            if (this.longPressTimerId) {
                clearTimeout(this.longPressTimerId);
                this.longPressTimerId = undefined;
            }
        };
        this.pressHandlers = {
            onPressStart: this.handlePressStart,
            onPressMove: this.handlePressMove,
            onPressEnd: this.handlePressEnd,
            onPressCancel: this.handlePressCancel,
        };
    }
}
exports.PressInterpreter = PressInterpreter;
//# sourceMappingURL=PressInterpreter.js.map